import { useCallback, useState } from "react";

/**
 * Direction of a column sort. "default" means "don't sort — use the upstream
 * order as-is" (useful so the user can revert to the API ordering).
 */
export type SortDir = "asc" | "desc" | "default";

export interface SortState<K extends string> {
  key: K | null;
  dir: SortDir;
}

/**
 * useSort manages a (key, dir) state with click-to-toggle semantics :
 *   click new column → desc
 *   click same column → asc
 *   click again      → default (revert to upstream order)
 *
 * Returns `setSort` keyed by column id, plus a `compare` helper to feed
 * Array.prototype.sort. Sorting on a column that wasn't requested by the
 * user just returns 0 (stable preserve).
 */
export function useSort<K extends string, T>(
  initialKey: K | null = null,
  initialDir: SortDir = "default"
) {
  const [state, setState] = useState<SortState<K>>({ key: initialKey, dir: initialDir });

  const toggle = useCallback((key: K) => {
    setState((prev) => {
      if (prev.key !== key) return { key, dir: "desc" };
      if (prev.dir === "desc") return { key, dir: "asc" };
      return { key: null, dir: "default" };
    });
  }, []);

  /**
   * sort returns a NEW array sorted according to the current state.
   * `accessor` maps a row to the primitive value to compare.
   */
  const sort = useCallback(
    (rows: T[], accessors: Partial<Record<K, (row: T) => unknown>>) => {
      if (state.key == null || state.dir === "default") return rows;
      const get = accessors[state.key];
      if (!get) return rows;
      const sign = state.dir === "asc" ? 1 : -1;
      // Sort a copy so we don't mutate the underlying React Query data.
      return [...rows].sort((a, b) => sign * cmp(get(a), get(b)));
    },
    [state]
  );

  return { state, toggle, sort };
}

function cmp(a: unknown, b: unknown): number {
  // Nullish values sort to the END regardless of direction (so an asc on
  // "last_post_at" doesn't bury threads with no posts at the top).
  const aN = a == null;
  const bN = b == null;
  if (aN && bN) return 0;
  if (aN) return 1;
  if (bN) return -1;
  if (typeof a === "number" && typeof b === "number") {
    if (a < b) return -1;
    if (a > b) return 1;
    return 0;
  }
  // Date-like strings (ISO) compare lexicographically OK.
  if (typeof a === "string" && typeof b === "string") {
    return a.localeCompare(b, "fr", { numeric: true });
  }
  // Generic fallback.
  return String(a).localeCompare(String(b), "fr", { numeric: true });
}
